'use client';

import { useState } from 'react';

interface ToolBlockProps {
  name: string;
  input: Record<string, unknown>;
  output?: string;
  status: 'running' | 'done' | 'error';
}

const MAX_LINES = 12;

function formatParts(name: string, input: Record<string, unknown>): { tool: string; detail: string } {
  const path = String(input.path || '');
  const offset = input.offset ? `:${input.offset}` : '';
  const limit = input.limit ? `-${Number(input.offset || 1) + Number(input.limit)}` : '';
  const lineRange = offset ? `${offset}${limit}` : '';

  switch (name) {
    case 'Read': case 'read':
      return { tool: 'read', detail: `${path}${lineRange}` };
    case 'Edit': case 'edit':
      return { tool: 'edit', detail: path };
    case 'Write': case 'write':
      return { tool: 'write', detail: path };
    case 'Bash': case 'bash': {
      const cmd = String(input.command || '');
      return { tool: '$', detail: cmd.length > 120 ? cmd.substring(0, 120) + '…' : cmd };
    }
    case 'grep':
      return { tool: 'search', detail: String(input.pattern || input.query || '') };
    default:
      return { tool: name.toLowerCase(), detail: '' };
  }
}

// Cursor-matched colors per tool type
function getToolStyle(name: string): { bg: string; border: string } {
  switch (name) {
    case 'Read': case 'read':
      return { bg: '#1a2618', border: '#3a6a3a' };
    case 'Edit': case 'edit':
    case 'Write': case 'write':
      return { bg: '#2a2618', border: '#6a6a3a' };
    case 'Bash': case 'bash':
      return { bg: '#1e2028', border: '#3a3a5a' };
    default:
      return { bg: '#1e2028', border: '#3a3a5a' };
  }
}

function OutputContent({ output, name }: { output: string; name: string }) {
  const [showAll, setShowAll] = useState(false);
  const lines = output.split('\n');
  const isLong = lines.length > MAX_LINES;
  const hiddenCount = lines.length - MAX_LINES;
  const isEdit = name === 'Edit' || name === 'edit';

  const displayLines = showAll ? lines : lines.slice(0, MAX_LINES);

  return (
    // overflowX auto — long tool-output lines scroll inside the block, never widen the chat (Boss 2026-07-10 side-scroll)
    <div style={{ fontFamily: 'var(--font-mono, monospace)', fontSize: '13px', lineHeight: '1.6', overflowX: 'auto' }}>
      {displayLines.map((line, i) => {
        // Diff lines
        if (isEdit || line.startsWith('+') || line.startsWith('-')) {
          if (line.startsWith('+')) return <div key={i} style={{ color: '#98c379', backgroundColor: '#1a261808' }}>{line}</div>;
          if (line.startsWith('-')) return <div key={i} style={{ color: '#e06c75', backgroundColor: '#2a161808' }}>{line}</div>;
        }
        // Comments
        if (line.trimStart().startsWith('//') || line.trimStart().startsWith('#')) {
          return <div key={i} style={{ color: '#5c6370' }}>{line}</div>;
        }
        return <div key={i} style={{ color: '#abb2bf' }}>{line}</div>;
      })}
      {isLong && !showAll && (
        <button
          onClick={(e) => { e.stopPropagation(); setShowAll(true); }}
          style={{ color: '#5c6370', fontSize: '12px', marginTop: '4px', cursor: 'pointer', background: 'none', border: 'none', padding: 0 }}
          onMouseEnter={e => (e.currentTarget.style.color = '#9ca3af')}
          onMouseLeave={e => (e.currentTarget.style.color = '#5c6370')}
        >
          ... ({hiddenCount} more lines, click to expand)
        </button>
      )}
    </div>
  );
}

export function ToolBlock({ name, input, output, status }: ToolBlockProps) {
  const [expanded, setExpanded] = useState(true);
  const { tool, detail } = formatParts(name, input);
  const { bg, border } = getToolStyle(name);

  return (
    <div
      className="my-2 rounded overflow-hidden"
      style={{ backgroundColor: bg, borderLeft: `2px solid ${border}` }}
    >
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center gap-2 px-4 py-2 text-left"
      >
        {status === 'running' && (
          <div className="w-3 h-3 border border-gray-500 border-t-gray-300 rounded-full animate-spin flex-shrink-0" />
        )}
        <span style={{ fontSize: '13px', fontFamily: 'var(--font-mono, monospace)' }} className="truncate flex-1">
          <span style={{ color: '#ffffff', fontWeight: 700 }}>{tool}</span>
          {detail && <span style={{ color: '#7ec699' }}>{' '}{detail}</span>}
        </span>
        <svg
          className={`w-3 h-3 flex-shrink-0 transition-transform ${!expanded ? '-rotate-90' : ''}`}
          style={{ color: '#5c6370' }}
          fill="none" viewBox="0 0 24 24" stroke="currentColor"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {expanded && output && (
        <div className="px-4 pb-3">
          <OutputContent output={output} name={name} />
        </div>
      )}
      {expanded && !output && status === 'running' && (
        <div className="px-4 pb-3 flex items-center gap-2">
          <div className="w-3 h-3 border border-gray-600 border-t-gray-400 rounded-full animate-spin" />
          <span style={{ color: '#5c6370', fontSize: '12px' }}>Working...</span>
        </div>
      )}
    </div>
  );
}
